import type { Hue } from '@tnn/core'
import { z } from 'zod'
import { type ShareGrant, type ShareName, shareKinds } from '../sharing'

export * as boardsLocales from './locales'

/**
 * Boards (apps/boards, apps/api/src/modules/boards): the office's projects as kanban boards. A board is its owner's
 * and is shared as a whole, as Drive's top folders are (a person, a branch, a department or the whole company, each as
 * a viewer or a member); its columns hold cards, a card one piece of work: who does it, by when, its checklist, files,
 * and what people say about it. A card in a column counted done is done: it waits on nobody.
 */

/**
 * A viewer sees the board, its cards and what is said there, and says something too; a member also makes, changes,
 * moves and archives cards and columns
 */
export const boardRoles = ['viewer', 'member'] as const
export type BoardRole = (typeof boardRoles)[number]

/** Where one stands on a board: its owner, or a role given to them (or their branch, department, the company) */
export type BoardAccess = BoardRole | 'owner'

const rank: Record<BoardAccess, number> = { viewer: 1, member: 2, owner: 3 }

/** What each may do beyond looking and commenting: change the cards and columns (`edit`), share, rename and archive the board */
const rights: Record<BoardAccess, { edit: boolean; own: boolean }> = {
  viewer: { edit: false, own: false },
  member: { edit: true, own: false },
  owner: { edit: true, own: true },
}
export type BoardRight = keyof (typeof rights)['owner']

/** May one who stands so do this (none: not on the board at all) */
export const boardMay = (access: BoardAccess | null | undefined, right: BoardRight) => !!access && rights[access][right]

/** Two ways onto a board, together: the one that may more (none: null) */
export function higherBoardAccess(a: BoardAccess | null, b: BoardAccess | null): BoardAccess | null {
  if (!a || !b) return a ?? b
  return rank[a] >= rank[b] ? a : b
}

/** A board's colour, its mark in lists: a hue of the apps' palette (`--tnn-hue-*`) */
export const boardColors = ['violet', 'blue', 'teal', 'green', 'amber', 'orange', 'red', 'pink'] as const satisfies readonly Hue[]
export type BoardColor = (typeof boardColors)[number]

/** A label's colour on a board (its name says what it means), a hue of the palette too */
export const labelColors = ['green', 'amber', 'orange', 'red', 'violet', 'blue', 'teal', 'grey'] as const satisfies readonly Hue[]
export type LabelColor = (typeof labelColors)[number]

export const BOARD_NAME_MAX = 120
export const COLUMN_NAME_MAX = 60
export const LABEL_NAME_MAX = 40
export const CARD_TITLE_MAX = 300
export const CARD_TEXT_MAX = 10_000
export const CHECK_TEXT_MAX = 300
export const BOARD_COLUMNS_MAX = 20
export const CARD_PEOPLE_MAX = 20
export const CARD_FILES_MAX = 20
export const CARD_CHECKS_MAX = 100

/**
 * How a card's due date stands today: done (its column is counted done), past (`over`), today, within the week
 * (`soon`), or later; none without a date. Dates are Thai calendar days, YYYY-MM-DD
 */
export type DueState = 'done' | 'over' | 'today' | 'soon' | 'later'

export function dueState(dueDate: string | null, today: string, done: boolean): DueState | null {
  if (!dueDate) return null
  if (done) return 'done'
  if (dueDate < today) return 'over'
  if (dueDate === today) return 'today'
  const week = new Date(`${today}T00:00:00Z`)
  week.setUTCDate(week.getUTCDate() + 7)
  return dueDate <= week.toISOString().slice(0, 10) ? 'soon' : 'later'
}

const text = (max: number, empty = 'กรุณาตั้งชื่อ') => z.string().trim().min(1, empty).max(max)

/** A new board: its columns from the start, the last counted done (none: the starter set in the board owner's language) */
export const boardInput = z.object({
  name: text(BOARD_NAME_MAX),
  color: z.enum(boardColors).default('violet'),
  columns: z.array(text(COLUMN_NAME_MAX)).min(1).max(BOARD_COLUMNS_MAX).optional(),
})
export type BoardInput = z.input<typeof boardInput>

export const boardPatch = z.object({ name: text(BOARD_NAME_MAX).optional(), color: z.enum(boardColors).optional() })
export type BoardPatch = z.input<typeof boardPatch>

export const columnInput = z.object({ name: text(COLUMN_NAME_MAX), done: z.boolean().default(false) })
export type ColumnInput = z.input<typeof columnInput>

export const columnPatch = z.object({ name: text(COLUMN_NAME_MAX).optional(), done: z.boolean().optional() })
export type ColumnPatch = z.input<typeof columnPatch>

/** A column put after another of its board (null: first) */
export const columnMove = z.object({ afterId: z.string().min(1).nullable() })

export const cardInput = z.object({ columnId: z.string().min(1), title: text(CARD_TITLE_MAX, 'กรุณาพิมพ์ชื่อการ์ด') })
export type CardInput = z.input<typeof cardInput>

/** Changing a card; people given it anew are told, and follow it */
export const cardPatch = z.object({
  title: text(CARD_TITLE_MAX, 'กรุณาพิมพ์ชื่อการ์ด').optional(),
  description: z.string().trim().max(CARD_TEXT_MAX).optional(),
  dueDate: z.iso.date().nullable().optional(),
  assigneeIds: z.array(z.string().min(1)).max(CARD_PEOPLE_MAX).optional(),
  labelIds: z.array(z.string().min(1)).max(10).optional(),
})
export type CardPatch = z.input<typeof cardPatch>

/** A card put into a column, right after another card of it (null: on top) */
export const cardMove = z.object({ columnId: z.string().min(1), afterId: z.string().min(1).nullable() })
export type CardMove = z.input<typeof cardMove>

export const checkInput = z.object({ text: text(CHECK_TEXT_MAX, 'กรุณาพิมพ์รายการ') })
export const checkPatch = z.object({ text: text(CHECK_TEXT_MAX, 'กรุณาพิมพ์รายการ').optional(), done: z.boolean().optional() })
export type CheckPatch = z.input<typeof checkPatch>

export const labelInput = z.object({ name: z.string().trim().max(LABEL_NAME_MAX).default(''), color: z.enum(labelColors) })
export type LabelInput = z.input<typeof labelInput>
export const labelPatch = labelInput.partial()

/** A comment on a card; the people it names with @ (who see the board) are told */
export const cardCommentInput = z.object({
  body: z.string().trim().min(1).max(4000),
  mentions: z.array(z.string().min(1)).max(20).default([]),
})
export type CardCommentInput = z.input<typeof cardCommentInput>

/** A file uploaded (`POST /api/files`) put on a card */
export const cardFileInput = z.object({ fileId: z.string().min(1) })

export const boardGrantInput = z.object({
  kind: z.enum(shareKinds),
  /** The person's account, the branch, the department; the company: `SHARE_COMPANY` */
  targetId: z.string().min(1),
  role: z.enum(boardRoles),
})
export type BoardGrantInput = z.input<typeof boardGrantInput>

/** Who may see a board, all of them at once (its owner is not among them) */
export const boardGrantsInput = z.object({ grants: z.array(boardGrantInput).max(200) })

export type BoardGrant = ShareGrant<BoardRole>

export interface BoardPerson {
  id: string
  name: string
}

/** A board in a list: mine, or shared with me (how it reached me), or put away */
export interface BoardSummary {
  id: string
  name: string
  color: BoardColor
  owner: BoardPerson | null
  access: BoardAccess
  /** Whom it is shared with (none: only its owner) */
  sharedWith: ShareName[]
  /** Shared with me: how it reached me (me, my branch, my department, the company) */
  via?: ShareName
  starred: boolean
  /** Cards on it (not put away), and those given to me not yet done */
  cards: number
  mine: number
  archivedAt: string | null
  updatedAt: string
}

export interface BoardLabel {
  id: string
  name: string
  color: LabelColor
}

/** A card as a column shows it */
export interface CardSummary {
  id: string
  columnId: string
  title: string
  dueDate: string | null
  /** Its column is counted done */
  done: boolean
  labelIds: string[]
  assignees: BoardPerson[]
  checklist: { done: number; total: number }
  files: number
  comments: number
  /** It says more than its title */
  described: boolean
}

export interface BoardColumn {
  id: string
  name: string
  /** Cards here are done */
  done: boolean
  cards: CardSummary[]
}

/** A board opened: where I stand, its columns and cards in order, its labels */
export interface BoardView {
  board: Omit<BoardSummary, 'cards' | 'mine'>
  columns: BoardColumn[]
  labels: BoardLabel[]
  /** Its owner, then those it is shared with by name (a branch's or department's people are not listed) */
  members: BoardPerson[]
}

export interface CardCheck {
  id: string
  text: string
  done: boolean
}

export interface CardFile {
  /** The attachment's own id (taken off by it) */
  id: string
  fileId: string
  name: string
  type: string
  size: number
  addedBy: BoardPerson | null
  addedAt: string
}

/** A card opened: all of it, where it is, and what happened to it and was said (newest first, as a record's activity) */
export interface CardDetail {
  card: CardSummary & { description: string; createdBy: BoardPerson | null; createdAt: string; archivedAt: string | null }
  board: { id: string; name: string; color: BoardColor; access: BoardAccess }
  column: { id: string; name: string; done: boolean }
  labels: BoardLabel[]
  checklist: CardCheck[]
  files: CardFile[]
  /** Those who are told about it: who was given it, and whoever chose to */
  followers: BoardPerson[]
  following: boolean
}

/** A card given to me and not done, from any board I see */
export interface MyCard {
  card: CardSummary
  board: { id: string; name: string; color: BoardColor }
  column: { id: string; name: string }
  labels: BoardLabel[]
}

/** A card put away, from the boards I may change */
export interface ArchivedCard {
  id: string
  title: string
  board: { id: string; name: string; color: BoardColor }
  column: string
  archivedAt: string
  archivedBy: BoardPerson | null
  /** I own its board: I may delete it for good */
  mayDelete: boolean
}

/** What is put away: my boards, and cards of boards I may change */
export interface BoardArchive {
  boards: BoardSummary[]
  cards: ArchivedCard[]
}
